import { parseApiError } from '@/api/api-axios';
import type { ParsedApiError } from '@/api/api-axios';
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/components/ui/use-toast';
import { resolveHref } from '@/utils/resolve-href';

import { FRONTEND_ERROR_CATALOG } from './error-catalog';
import type { ErrorAction, ErrorEntry, ShowErrorToastOptions } from './types';

/**
 * WeakSet tracking errors that have already been shown to the user via
 * showErrorToast(). The global MutationCache.onError handler checks this
 * to avoid showing a duplicate toast for errors already handled in
 * component-level try/catch blocks.
 */
export const handledErrors = new WeakSet<object>();

function buildNavigateAction(action: ErrorAction) {
  return (
    <ToastAction altText={action.label} asChild>
      <a href={resolveHref(action.href)}>{action.label}</a>
    </ToastAction>
  );
}

function buildRetryAction(onRetry: () => void) {
  return (
    <ToastAction altText="Try again" onClick={onRetry}>
      Try Again
    </ToastAction>
  );
}

export function showErrorToast(
  error: unknown,
  fallbackMessage: string,
  options?: ShowErrorToastOptions
): ParsedApiError {
  const parsed = parseApiError(error, fallbackMessage);

  const catalogEntry: ErrorEntry | undefined = parsed.code ? FRONTEND_ERROR_CATALOG[parsed.code] : undefined;
  const effective: ErrorEntry = {
    title: fallbackMessage,
    variant: 'destructive',
    ...catalogEntry,
    ...options?.overrides,
  };

  // Build action: retry takes precedence over navigate action
  let action: React.ReactElement | undefined;
  if (options?.onRetry) {
    action = buildRetryAction(options.onRetry);
  } else if (effective.action) {
    action = buildNavigateAction(effective.action);
  }

  // Mark the error as handled so the global MutationCache.onError handler
  // won't show a duplicate toast for the same error.
  if (error !== null && typeof error === 'object') {
    handledErrors.add(error);
  }

  toast({
    action,
    description: parsed.message !== effective.title ? parsed.message : undefined,
    duration: effective.persistent === true ? Infinity : undefined,
    id: options?.toastId,
    title: effective.title,
    variant: effective.variant ?? 'destructive',
  });

  return parsed;
}
